/**
 * Vorlagen fuer das Schutzklassenkonzept.
 *
 * Katalog im Repo, Fassung am Datensatz — Begruendung wie in `ebvKlassen.ts`.
 *
 * ═══ WARUM ES UEBERHAUPT KLASSEN GIBT ═════════════════════════════════════
 * Weil die Alternative schlechter ist. Ein A+S-Plan, der fuer die ganze
 * Baustelle „Vollschutz" anordnet, ist bequem aufgeschrieben und wird nicht
 * eingehalten: wer im Weissbereich eine Vollmaske tragen soll, traegt sie
 * irgendwann auch im Schwarzbereich nicht. Die BG BAU benennt beides als
 * Mangel — pauschale PSA fuer alle Taetigkeiten UND „inadaequate oder
 * ueberdimensionierte Ausruestung".
 *
 * Die Klassen sind ausdruecklich VORLAGEN. Ein Plan darf sie umbenennen,
 * ergaenzen und anders schneiden; die Stufenlogik rechnet auf `rang`, nicht
 * auf dem Namen.
 *
 * ═══ WAS AN DIESEN VORLAGEN KONKRET IST ═══════════════════════════════════
 * Unbestimmte Begriffe wie „Einwegchemikalienanzug" oder „geeigneter
 * Filteratemschutz" sind laut BG BAU keine Festlegung, sondern deren
 * Vermeidung. Deshalb tragen die Vorlagen die Angaben, die TRGS 524 Anlage 8
 * verlangt: Atemschutzsystem, Anschluss, Filtertyp und -klasse, Wechsel-
 * intervall und -kriterien, Anzugtyp, Handschuhmaterial und Konfektionierung.
 *
 * Der Filtertyp ist bewusst NICHT vorbelegt: er folgt aus dem Stoff, nicht
 * aus der Schutzklasse. Ein Plan mit Quecksilberdampf braucht Hg-P3, einer
 * mit partikelgebundenen PAK nur P3 — dieselbe Klasse, anderer Filter.
 * `asplanLogic.ts` prueft das gegen die gefuehrten Stoffe.
 */

import type { Atemschutz, Fussschutz, Handschutz, Schutzkleidung } from '../types';

export const SCHUTZKLASSEN_KATALOG_VERSION = '2026-08-22';

export interface SchutzklassenVorlage {
    key: string;
    rang: number;
    bezeichnung: string;
    anwendungsbereich: string;
    psa: { text: string; iso?: string }[];
    zusatz: { text: string }[];
    atemschutz: Atemschutz;
    schutzkleidung: Schutzkleidung;
    handschutz: Handschutz;
    fussschutz: Fussschutz;
    /** Geraetegruppe fuer die Tragezeitrechnung (`tragezeit.ts`). */
    geraetegruppe?: string;
    farbe: string;
}

export const SCHUTZKLASSEN_VORLAGEN: SchutzklassenVorlage[] = [
    {
        key: 'SK1',
        rang: 1,
        bezeichnung: 'Basisschutz',
        anwendungsbereich:
            'Weißbereich, Kabinenarbeitsplätze mit Schutzbelüftung (Filter H13), '
            + 'unbelastete Bereiche und Nebenarbeiten ohne Expositionspotenzial.',
        psa: [
            { text: 'Arbeitskleidung lang, körperbedeckend' },
            { text: 'Sicherheitsschuhe S3', iso: 'M008' },
            { text: 'Schutzhandschuhe Nitril', iso: 'M009' },
            { text: 'Schutzhelm', iso: 'M014' },
            { text: 'Warnweste', iso: 'M015' },
        ],
        zusatz: [
            { text: 'Hände waschen vor Pause und Schichtende' },
            { text: 'Kein Atemschutz erforderlich, solange die Kabinenbelüftung wirksam ist' },
            { text: 'Filterwechsel der Kabinenanlagen nach Filterbuch' },
        ],
        atemschutz: {},
        schutzkleidung: { hinweis: 'kein Chemikalienschutzanzug erforderlich' },
        handschutz: { material: 'Nitril', gefaehrdungen: ['mechanisch'] },
        fussschutz: { typ: 'S3', gefaehrdungen: ['mechanisch'] },
        farbe: '#dcfce7',
    },
    {
        key: 'SK2',
        rang: 2,
        bezeichnung: 'Standardschutz Schwarzbereich',
        anwendungsbereich:
            'Arbeiten im Schwarzbereich mit belastetem Material, Regelfall der '
            + 'Erd- und Aushubarbeiten sowie kurzzeitiger Begehungen.',
        psa: [
            { text: 'wie SK1, zusätzlich:' },
            { text: 'Einwegschutzanzug Typ 5/6, Kategorie III', iso: 'M010' },
            { text: 'Halbmaske mit Partikelfilter', iso: 'M017' },
            { text: 'Schutzhandschuhe nitril- oder butylbeschichtet', iso: 'M009' },
            { text: 'Schutzbrille oder Visier bei Spritzgefahr', iso: 'M004' },
        ],
        zusatz: [
            { text: 'Eintritt und Austritt ausschließlich über die Schwarz-Weiß-Anlage' },
            { text: 'Hände und Gesicht waschen, Duschen am Schichtende' },
            { text: 'Filterwechsel mindestens schichtweise oder bei steigendem Atemwiderstand' },
            { text: 'Befeuchtung des Bodens gegen Staubfreisetzung' },
        ],
        atemschutz: {
            system: 'filter',
            geraet: 'Halbmaske',
            anschluss: 'Halbmaske',
            geblaese: false,
            wechsel_standard: 'schichtweise, zu Pausen und Schichtende',
            wechsel_kriterien: [
                'spürbar erhöhter Atemwiderstand',
                'Geruchs- oder Geschmacksdurchbruch',
                'sichtbare Durchfeuchtung oder Beschädigung',
            ],
        },
        schutzkleidung: { kategorie: 'III', typ: '5/6', abkleben: true },
        handschutz: {
            material: 'Nitril oder Butylkautschuk',
            buendchen: 'dicht ansitzend, zum Anzug abgeklebt',
            gefaehrdungen: ['mechanisch', 'chemisch'],
        },
        fussschutz: { typ: 'S3', gefaehrdungen: ['mechanisch', 'chemisch'] },
        geraetegruppe: 'filter_halbmaske',
        farbe: '#fef08a',
    },
    {
        key: 'SK3',
        rang: 3,
        bezeichnung: 'Erhöhter Schutz',
        anwendungsbereich:
            'Bereiche mit durchgängiger Belastung, hoher Expositionsdauer oder '
            + 'Aerosolbildung; Tätigkeiten unmittelbar am belasteten Material.',
        psa: [
            { text: 'wie SK2, jedoch:' },
            { text: 'Vollmaske mit Partikelfilter ODER Gebläsehaube TH3P', iso: 'M017' },
            { text: 'doppelte Handschuhe (innen Nitril, außen Butyl)', iso: 'M009' },
            { text: 'Einwegschutzanzug Typ 4/5/6, Kategorie III', iso: 'M010' },
        ],
        zusatz: [
            { text: 'Tragezeitbegrenzung nach DGUV Regel 112-190 beachten und dokumentieren' },
            { text: 'Ständige Aufsicht, keine Alleinarbeit' },
            { text: 'Verstärkte messtechnische Ueberwachung' },
            { text: 'Eintrag im Schleusenbuch verpflichtend' },
        ],
        atemschutz: {
            system: 'filter',
            geraet: 'Vollmaske oder Gebläsehaube TH3P',
            anschluss: 'Vollmaske / Haube',
            geblaese: false,
            wechsel_standard: 'schichtweise, zu Pausen und Schichtende',
            wechsel_kriterien: [
                'spürbar erhöhter Atemwiderstand',
                'Geruchs- oder Geschmacksdurchbruch',
                'nach jedem Ausschleusen bei sichtbarer Kontamination',
            ],
        },
        schutzkleidung: { kategorie: 'III', typ: '4/5/6', abkleben: true, rollring: true },
        handschutz: {
            material: 'innen Nitril, außen Butylkautschuk',
            doppelt: true,
            buendchen: 'Rollringsystem zur Schutzkleidung',
            gefaehrdungen: ['mechanisch', 'chemisch'],
        },
        fussschutz: {
            typ: 'S5',
            gefaehrdungen: ['mechanisch', 'chemisch'],
            hinweis: 'halbhohe Stiefel, im Schwarzbereich verbleibend',
        },
        geraetegruppe: 'filter_vollmaske',
        farbe: '#fb923c',
    },
];

const NACH_KEY = new Map(SCHUTZKLASSEN_VORLAGEN.map(v => [v.key, v]));

export const schutzklassenVorlage = (key: string): SchutzklassenVorlage | null =>
    NACH_KEY.get(key) ?? null;

/**
 * Handhabungshinweise, die fuer jede Klasse ab SK2 gelten (Check 16).
 *
 * Sie stehen getrennt von den Klassen, weil sie sich nicht je Stufe
 * unterscheiden — was sich nicht unterscheidet, dreimal zu schreiben, heisst
 * es dreimal pflegen zu muessen und zweimal zu vergessen.
 */
export const HANDHABUNG_VORLAGE = [
    {
        thema: 'Lagerung',
        text: 'PSA trocken und staubfrei in einem eigenen Container oder Schrank lagern. '
            + 'Ablegen im kontaminierten Bereich ist unzulässig.',
    },
    {
        thema: 'Wartung und Prüfung',
        text: 'Atemschutzgeräte werden von einem benannten Gerätewart gewartet und geprüft; '
            + 'Prüfungen sind im Gerätebuch festzuhalten.',
    },
    {
        thema: 'Gebrauchte Einweg-PSA',
        text: 'Bei jeder Arbeitsunterbrechung in den bereitgestellten Behältern entsorgen — '
            + 'keine Mehrfachverwendung von Einweganzügen und Filtermasken.',
    },
    {
        thema: 'Kontaminierte Mehrweg-PSA',
        text: 'Vor dem Ausschleusen reinigen oder staubdicht verpacken. Haftet Material an, '
            + 'wird die Schutzkleidung unverzüglich gewechselt, nicht abgebürstet.',
    },
    {
        thema: 'Betriebsanweisung und Unterweisung',
        text: 'Trägerkreis, Anlege- und Ablegereihenfolge, Dichtsitzprüfung und '
            + 'Wechselkriterien sind in der Betriebsanweisung des Auftragnehmers zu regeln '
            + 'und Gegenstand der Unterweisung.',
    },
];
